import { issueTexts } from "@nexxt/app-kit";
import type { PriceChannel } from "@nexxt/contracts/catalog";
import type { NumberedDocumentType } from "@nexxt/contracts/documents";
import type {
  DatasetGroup,
  DatasetKey,
  datasets,
  ErpInvoiceDirection,
  LocationType,
  PaymentCategory,
  ReferenceIssueRule,
  TaxDestination,
} from "@nexxt/contracts/reference";
import type { SoStatus } from "@nexxt/contracts/sales";
import { m } from "../../../paraglide/messages.js";
import type { FieldError, Lookup } from "./record-form";

// The words of the reference data: a dataset, a field or an option the contract gains needs its text here, or the
// app does not compile.

export const groupLabel: Record<DatasetGroup, () => string> = {
  product: m.reference_group_product,
  campaign: m.reference_group_campaign,
  commercial: m.reference_group_commercial,
  company: m.reference_group_company,
};

export const datasetLabel: Record<DatasetKey, () => string> = {
  season: m.reference_dataset_season,
  category: m.reference_dataset_category,
  gender: m.reference_dataset_gender,
  product_category: m.reference_dataset_product_category,
  product_subcategory: m.reference_dataset_product_subcategory,
  taxonomy_rule: m.reference_dataset_taxonomy_rule,
  size: m.reference_dataset_size,
  size_run_category: m.reference_dataset_size_run_category,
  size_run: m.reference_dataset_size_run,
  colour: m.reference_dataset_colour,
  colour_combination: m.reference_dataset_colour_combination,
  customs_classification: m.reference_dataset_customs_classification,
  weight_unit: m.reference_dataset_weight_unit,
  price_list: m.reference_dataset_price_list,
  payment_terms: m.reference_dataset_payment_terms,
  incoterm: m.reference_dataset_incoterm,
  delivery_term: m.reference_dataset_delivery_term,
  showroom_location: m.reference_dataset_showroom_location,
  currency: m.reference_dataset_currency,
  country: m.reference_dataset_country,
  legal_entity: m.reference_dataset_legal_entity,
  location: m.reference_dataset_location,
  tax_rule: m.reference_dataset_tax_rule,
  holiday: m.reference_dataset_holiday,
  hubspot_deal_stage: m.reference_dataset_hubspot_deal_stage,
  document_number_series: m.reference_dataset_document_number_series,
  erp_vat_code: m.reference_dataset_erp_vat_code,
  erp_invoice_setup: m.reference_dataset_erp_invoice_setup,
};

/** Every field name of every dataset. */
type FieldName = { [K in DatasetKey]: keyof (typeof datasets)[K]["fields"] }[DatasetKey];

const fieldLabels: Record<FieldName, () => string> = {
  code: m.reference_field_code,
  name: m.col_name,
  key: m.reference_field_key,
  year: m.reference_field_year,
  archived: m.reference_field_archived,
  genderId: m.field_gender,
  productCategoryId: m.field_product_category,
  productSubcategoryId: m.field_product_subcategory,
  sizeRunCategoryId: m.field_size_run_category,
  sizeRunId: m.field_size_run,
  abbreviation: m.reference_field_abbreviation,
  sortOrder: m.reference_field_sort_order,
  sizeIds: m.reference_field_sizes,
  code2: m.reference_field_code2,
  code3: m.reference_field_code3,
  baseColourId: m.reference_field_base_colour,
  secondaryColourId: m.reference_field_secondary_colour,
  variations: m.reference_field_variations,
  channel: m.setup_channel,
  region: m.reference_field_region,
  defaultCurrency: m.reference_field_default_currency,
  category: m.col_category,
  netDays: m.reference_field_net_days,
  depositPct: m.reference_field_deposit_pct,
  paymentTiming: m.reference_field_payment_timing,
  hilldunTermCode: m.reference_field_hilldun_term_code,
  description: m.reference_field_description,
  allocationRank: m.reference_field_allocation_rank,
  incotermCode: m.reference_field_incoterm,
  city: m.reference_field_city,
  timezone: m.reference_field_timezone,
  symbol: m.reference_field_symbol,
  decimals: m.reference_field_decimals,
  iso3: m.reference_field_iso3,
  nativeName: m.reference_field_native_name,
  cultureCode: m.reference_field_culture_code,
  currencyCode: m.reference_field_currency,
  isEu: m.reference_field_is_eu,
  isVisible: m.reference_field_is_visible,
  countryCode: m.reference_field_country,
  baseCurrency: m.reference_field_base_currency,
  bcCompanyId: m.reference_field_bc_company_id,
  allocationEnabled: m.reference_field_allocation_enabled,
  invoiceAfterShipment: m.reference_field_invoice_after_shipment,
  adjustmentGlAccount: m.reference_field_adjustment_gl_account,
  vatNumber: m.reference_field_vat_number,
  registeredAddress: m.reference_field_registered_address,
  senderEmail: m.reference_field_sender_email,
  depositCollectionEmail: m.reference_field_deposit_collection_email,
  erpItemFile: m.reference_field_erp_item_file,
  erpFolder: m.reference_field_erp_folder,
  erpProductType: m.reference_field_erp_product_type,
  legalEntityId: m.reference_field_legal_entity,
  type: m.reference_field_type,
  threePl: m.reference_field_three_pl,
  transferCustomerCode: m.reference_field_transfer_customer_code,
  taxCode: m.reference_field_tax_code,
  shipFromCountryCode: m.reference_field_ship_from_country,
  destinationType: m.reference_field_destination_type,
  destinationCountryCode: m.reference_field_destination_country,
  priority: m.reference_field_priority,
  validFrom: m.reference_field_valid_from,
  validTo: m.reference_field_valid_to,
  day: m.reference_field_day,
  soStatus: m.reference_field_so_status,
  pipeline: m.reference_field_pipeline,
  stage: m.reference_field_stage,
  documentType: m.reference_field_document_type,
  prefix: m.reference_field_prefix,
  pattern: m.reference_field_pattern,
  digits: m.reference_field_digits,
  firstYear: m.reference_field_first_year,
  firstNumber: m.reference_field_first_number,
  rate: m.reference_field_rate,
  purchaseScope: m.reference_field_purchase_scope,
  direction: m.reference_field_direction,
  protocol: m.reference_field_protocol,
  costCentre: m.reference_field_cost_centre,
  project: m.reference_field_project,
  costCentreCode: m.reference_field_cost_centre_code,
  costAccount: m.reference_field_cost_account,
};

export const fieldLabel = (name: string): string =>
  Object.hasOwn(fieldLabels, name) ? fieldLabels[name as FieldName]() : name;

const choiceLabels: Record<
  | PriceChannel
  | PaymentCategory
  | LocationType
  | TaxDestination
  | SoStatus
  | NumberedDocumentType
  | ErpInvoiceDirection,
  () => string
> = {
  exw: m.price_channel_exw,
  ddp: m.price_channel_ddp,
  agency: m.price_channel_agency,
  farfetch: m.price_channel_farfetch,
  open_terms: m.reference_payment_open_terms,
  proforma: m.reference_payment_proforma,
  warehouse: m.reference_location_warehouse,
  return_location: m.reference_location_return,
  faulty_goods: m.reference_location_faulty,
  transit: m.reference_location_transit,
  virtual: m.reference_location_virtual,
  domestic: m.reference_destination_domestic,
  eu: m.reference_destination_eu,
  extra_eu: m.reference_destination_extra_eu,
  any: m.reference_destination_any,
  // the status of a sales order a stage of HubSpot is for (ADR-236), in the words of the orders
  confirmed: m.so_status_confirmed,
  credit_blocked: m.so_status_credit_blocked,
  allocated: m.so_status_allocated,
  locked: m.so_status_locked,
  awaiting_payment: m.so_status_awaiting_payment,
  document_requested: m.so_status_document_requested,
  document_error: m.so_status_document_error,
  document_issued: m.so_status_document_issued,
  released_to_3pl: m.so_status_released_to_3pl,
  completed: m.so_status_completed,
  cancelled: m.so_status_cancelled,
  // the documents Nexxt numbers (C-48, ADR-255), in the words of the documents
  invoice: m.document_type_invoice,
  credit_note: m.document_type_credit_note,
  // the two sides of the invoices file to the ERP (ADR-256)
  purchase: m.reference_direction_purchase,
  sales: m.reference_direction_sales,
};

export const choiceLabel = (option: string): string =>
  Object.hasOwn(choiceLabels, option) ? choiceLabels[option as keyof typeof choiceLabels]() : option;

/** The words a cell is shown with: the names of the records it points to, an option, a flag. */
export const cellWords = (lookup: Lookup) => ({ lookup, choice: choiceLabel, yes: m.yes(), no: m.no() });

const fieldErrors: Record<FieldError, () => string> = {
  required: m.reference_error_required,
  invalid: m.reference_error_invalid,
};

export const fieldErrorText = (error: FieldError | undefined) => error && fieldErrors[error]();

const rules: Record<ReferenceIssueRule, () => string> = {
  taken: m.reference_issue_taken,
  unknown_reference: m.reference_issue_unknown_reference,
  after_start: m.issue_after_start,
  same_as_base: m.reference_issue_same_as_base,
  in_the_past: m.parameter_start_in_the_past,
};

/** What the domain says of a field of a refused save (422), next to its control. */
export const issueText = issueTexts(rules).fieldIssue;
